import { useEffect, useState } from "react";
import { fetchDevSuggestions, updateDevSuggestion, fetchDevSuggestionAlternatives } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { BookOpen, Briefcase, Award, Users, GraduationCap, CheckCircle2, XCircle, Sparkles, ExternalLink, Clock, Repeat, MessageSquare, Inbox } from "lucide-react";
import { toast } from "sonner";

const TYPE_META = {
  course: { label: "Eğitim", icon: BookOpen, color: "sky" },
  project: { label: "Proje", icon: Briefcase, color: "amber" },
  certification: { label: "Sertifika", icon: Award, color: "emerald" },
  coaching: { label: "Koçluk", icon: Users, color: "rose" },
  book: { label: "Kitap", icon: BookOpen, color: "violet" },
};

const STATUS_META = {
  pending: { label: "Beklemede", color: "amber" },
  accepted: { label: "Kabul edildi", color: "emerald" },
  declined: { label: "Reddedildi", color: "rose" },
  counter: { label: "Karşı öneri yapıldı", color: "violet" },
  completed: { label: "Tamamlandı", color: "sky" },
};

const PRIORITY_LABEL = { high: "Yüksek öncelik", medium: "Orta", low: "Düşük" };

function SuggestionCard({ item, onUpdate }) {
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState(null); // 'accept' | 'decline' | 'counter'
  const [response, setResponse] = useState("");
  const [counterTitle, setCounterTitle] = useState("");
  const [counterProvider, setCounterProvider] = useState("");
  const [counterUrl, setCounterUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [alts, setAlts] = useState(null);
  const [altsLoading, setAltsLoading] = useState(false);

  const typeMeta = TYPE_META[item.resource_type] || TYPE_META.course;
  const Icon = typeMeta.icon;
  const statusMeta = STATUS_META[item.status] || STATUS_META.pending;

  const handleAction = async (status) => {
    setBusy(true);
    try {
      const payload = { status, response_message: response };
      if (status === "counter") {
        if (!counterTitle.trim()) {
          toast.error("Karşı önerinin başlığı zorunlu");
          setBusy(false);
          return;
        }
        payload.counter_title = counterTitle;
        payload.counter_provider = counterProvider;
        payload.counter_url = counterUrl;
      }
      const updated = await updateDevSuggestion(item.id, payload);
      onUpdate(updated);
      toast.success(
        status === "accepted" ? "Öneriyi kabul ettin" :
        status === "declined" ? "Öneriyi reddettin" :
        "Karşı önerin gönderildi",
        { description: `${item.suggested_by_name} bilgilendirildi.` },
      );
      setMode(null);
      setResponse("");
    } catch (e) {
      toast.error("İşlem başarısız");
    } finally {
      setBusy(false);
    }
  };

  const loadAlts = async () => {
    setAltsLoading(true);
    try {
      const data = await fetchDevSuggestionAlternatives(item.id);
      setAlts(data.alternatives || []);
    } catch (e) {
      toast.error("Alternatifler yüklenemedi");
    } finally {
      setAltsLoading(false);
    }
  };

  return (
    <div className="card-surface p-5" data-testid={`dev-suggestion-${item.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-${typeMeta.color}-50 border border-${typeMeta.color}-200`}>
          <Icon className={`w-5 h-5 text-${typeMeta.color}-700`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className={`text-[0.65rem] uppercase tracking-wider text-${typeMeta.color}-700 font-medium`}>{typeMeta.label}</span>
            <Badge variant="outline" className="border-slate-300 text-slate-600 text-[0.65rem]">{item.skill_name}</Badge>
            {item.priority === "high" && (
              <span className="text-[0.6rem] uppercase tracking-wider text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">
                {PRIORITY_LABEL.high}
              </span>
            )}
          </div>
          <h4 className="font-semibold text-slate-900 text-base mb-1">{item.title}</h4>
          <div className="text-xs text-slate-500 flex items-center gap-3 flex-wrap">
            {item.provider && <span>{item.provider}</span>}
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {item.duration_weeks} hafta</span>
            {item.url && (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:underline">
                <ExternalLink className="w-3 h-3" /> Kaynağı aç
              </a>
            )}
          </div>
        </div>
        <div className="text-right space-y-1.5">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.7rem] font-medium border bg-${statusMeta.color}-50 text-${statusMeta.color}-800 border-${statusMeta.color}-200`}>
            {statusMeta.label}
          </span>
          <div className="text-[0.65rem] text-slate-400">
            {new Date(item.submitted_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-600 flex-wrap">
        <div className="flex items-center gap-2">
          <Avatar className="w-6 h-6">
            <AvatarImage src={item.suggested_by_avatar} />
            <AvatarFallback>{item.suggested_by_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <span>{item.suggested_by_name} <span className="text-slate-400">· {item.suggested_by_title}</span></span>
        </div>
        <button onClick={() => setExpanded((e) => !e)} className="ml-auto text-[0.7rem] text-sky-700 hover:underline" data-testid={`dev-toggle-${item.id}`}>
          {expanded ? "Kapat" : item.status === "pending" ? "Yanıtla / Aksiyon" : "Detay"}
        </button>
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          {item.message && (
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                <MessageSquare className="w-3 h-3" /> {item.suggested_by_name.split(" ")[0]}'in notu
              </div>
              <p className="text-sm text-slate-800 leading-relaxed">{item.message}</p>
            </div>
          )}
          {item.response_message && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[0.65rem] uppercase tracking-wider text-emerald-700 mb-1">Senin yanıtın</div>
              <p className="text-sm text-emerald-900 leading-relaxed">{item.response_message}</p>
            </div>
          )}
          {item.counter_proposal && (
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-3">
              <div className="text-[0.65rem] uppercase tracking-wider text-violet-700 mb-1 flex items-center gap-1">
                <Repeat className="w-3 h-3" /> Karşı öneriniz
              </div>
              <div className="text-sm text-violet-900 font-medium">{item.counter_proposal.title}</div>
              <div className="text-xs text-violet-700">{item.counter_proposal.provider}</div>
              {item.counter_proposal.url && (
                <a href={item.counter_proposal.url} target="_blank" rel="noopener noreferrer" className="text-xs text-violet-700 hover:underline inline-flex items-center gap-1 mt-1">
                  <ExternalLink className="w-3 h-3" /> Linki aç
                </a>
              )}
            </div>
          )}

          {item.status === "pending" && !mode && (
            <div className="flex gap-2 flex-wrap">
              <Button data-testid={`dev-accept-${item.id}`} onClick={() => setMode("accept")} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Kabul et
              </Button>
              <Button data-testid={`dev-counter-${item.id}`} onClick={() => setMode("counter")} variant="outline" className="flex-1 border-violet-300 text-violet-700 hover:bg-violet-50">
                <Repeat className="w-4 h-4 mr-1.5" /> Karşı öneri
              </Button>
              <Button data-testid={`dev-decline-${item.id}`} onClick={() => setMode("decline")} variant="outline" className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50">
                <XCircle className="w-4 h-4 mr-1.5" /> Reddet
              </Button>
            </div>
          )}

          {mode && (
            <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
              <Textarea
                data-testid={`dev-response-${item.id}`}
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder={mode === "accept" ? "Teşekkür notun (opsiyonel)…" : mode === "decline" ? "Nazikçe nedenini yaz…" : "Karşı öneri için bağlamını yaz…"}
                rows={2}
                className="bg-white resize-none text-sm"
              />
              {mode === "counter" && (
                <>
                  <Input value={counterTitle} onChange={(e) => setCounterTitle(e.target.value)} placeholder="Karşı önerin başlığı *" className="bg-white text-sm" data-testid={`dev-counter-title-${item.id}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={counterProvider} onChange={(e) => setCounterProvider(e.target.value)} placeholder="Sağlayıcı" className="bg-white text-sm" />
                    <Input value={counterUrl} onChange={(e) => setCounterUrl(e.target.value)} placeholder="Link (opsiyonel)" className="bg-white text-sm" />
                  </div>
                  <button onClick={loadAlts} disabled={altsLoading} className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1" data-testid={`dev-ai-alts-${item.id}`}>
                    <Sparkles className="w-3 h-3" /> {altsLoading ? "AI alternatif öneriyor…" : "AI'dan alternatif öner"}
                  </button>
                  {alts && (
                    <div className="space-y-1.5 pt-1">
                      {alts.map((a, i) => (
                        <button key={i} type="button" onClick={() => { setCounterTitle(a.title); setCounterProvider(a.provider); }} className="w-full text-left rounded border border-sky-200 bg-sky-50 hover:bg-sky-100 px-2.5 py-2 text-xs">
                          <div className="font-medium text-sky-900">{a.title}</div>
                          <div className="text-sky-700 text-[0.7rem]">{a.provider} · {a.duration_weeks} hf</div>
                          <div className="text-slate-600 text-[0.7rem] mt-0.5">{a.why_this}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
              <div className="flex gap-2">
                <Button onClick={() => handleAction(mode === "accept" ? "accepted" : mode === "decline" ? "declined" : "counter")} disabled={busy} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`dev-confirm-${item.id}`}>
                  {busy ? "Gönderiliyor…" : "Onayla ve gönder"}
                </Button>
                <Button onClick={() => { setMode(null); setAlts(null); }} variant="outline" className="border-slate-300">İptal</Button>
              </div>
            </div>
          )}

          {item.status === "accepted" && (
            <Button onClick={async () => {
              setBusy(true);
              try {
                const updated = await updateDevSuggestion(item.id, { status: "completed", response_message: response });
                onUpdate(updated);
                toast.success("Tamamlandı olarak işaretlendi");
              } finally { setBusy(false); }
            }} variant="outline" className="border-sky-300 text-sky-800 hover:bg-sky-50">
              <GraduationCap className="w-4 h-4 mr-2" /> Tamamlandı olarak işaretle
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function DevelopmentSuggestionsPanel({ employeeId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const d = await fetchDevSuggestions({ employee_id: employeeId });
      setItems(d.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const handleUpdate = (updated) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const pending = items.filter((i) => i.status === "pending").length;

  if (loading) {
    return <div className="card-surface p-6"><div className="h-3 bg-slate-200/60 rounded animate-pulse w-1/3" /></div>;
  }

  if (items.length === 0) {
    return (
      <div className="card-surface p-8 text-center" data-testid="dev-suggestions-empty">
        <Inbox className="w-9 h-9 mx-auto text-slate-300 mb-3" />
        <h4 className="font-display text-base font-semibold text-slate-900">Henüz gelişim önerisi yok</h4>
        <p className="text-sm text-slate-500 mt-1">Yöneticin veya İK ekibi sana eğitim/kurs önerdiğinde burada görünecek.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="dev-suggestions-panel">
      <div className="card-surface p-5 bg-gradient-to-br from-amber-50 via-white to-white border-amber-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-300 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-amber-700 mb-1">Gelen Gelişim Önerileri</div>
            <h4 className="font-display text-lg font-semibold text-slate-900">
              {pending > 0
                ? <><span className="text-amber-700">{pending}</span> öneri yanıtını bekliyor</>
                : "Tüm öneriler değerlendirildi"}
            </h4>
            <p className="text-xs text-slate-600 mt-1">
              Yöneticin ve İK ekibinin sana gönderdiği gelişim önerileri (eğitim, proje, sertifika, koçluk, kitap).
              Kabul edebilir, reddedebilir veya karşı öneri yapabilirsin.
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((it) => (
          <SuggestionCard key={it.id} item={it} onUpdate={handleUpdate} />
        ))}
      </div>
    </div>
  );
}
